import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, ArrowRight, Trophy, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";

/**
 * Gamified Money Personality Quiz
 * 
 * Comprehensive 2-3 minute quiz to capture deep user data for personalization.
 * Features 6 sections with progress tracking, animations, and engaging interactions.
 * 
 * Sections:
 * 1. The Basics - Personal info and demographics
 * 2. Money Personality - Relationship with money and apps
 * 3. Dreams & Goals - Aspirations and lifestyle vision
 * 4. Current Situation - Financial behavior and status
 * 5. Preferences - Product fit and feature interest
 * 6. Spread the Word - Referral and community engagement
 * 
 * @returns {JSX.Element} Full-screen gamified quiz experience
 */
export const MoneyQuizPage = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  
  // Quiz state management
  const [currentSection, setCurrentSection] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showResults, setShowResults] = useState(false);
  
  // Form data for all sections
  const [quizData, setQuizData] = useState({
    // Section 1: The Basics
    name: "",
    email: "",
    age: "",
    location: "",
    gender: "",
    
    // Section 2: Money Personality
    moneyRelationship: "",
    moneyApps: [] as string[],
    bankProvider: "",
    balanceCheckFreq: "",
    moneyKnowledge: [5],
    moneyEducation: "",
    
    // Section 3: Dreams & Goals
    dreamJob: "",
    dreamSalary: "",
    retireAge: "",
    dreamHouse: "",
    dreamCar: "",
    startBusiness: "",
    wantToInvest: "",
    cryptoInterest: "",
    
    // Section 4: Current Situation
    currentlySaving: "",
    monthlySavings: [50],
    trackSpending: "",
    budgetConfidence: [5],
    hasDebt: "",
    debtType: "",
    hasInvested: "",
    investmentType: "",
    
    // Section 5: Preferences
    wantAiAssistant: "",
    bankConnection: "",
    financialGame: "",
    excitingFeatures: [] as string[],
    
    // Section 6: Spread the Word
    referralInterest: "",
    socialHandle: "",
    betaTester: "",
    ambassador: ""
  });

  // Quiz sections configuration
  const sections = [
    { title: "The Basics", icon: "👋", color: "bg-primary" },
    { title: "Money Personality", icon: "🧠", color: "bg-secondary" },
    { title: "Dreams & Goals", icon: "🪄", color: "bg-accent" },
    { title: "Current Situation", icon: "💸", color: "bg-primary" },
    { title: "Preferences", icon: "🤖", color: "bg-secondary" },
    { title: "Spread the Word", icon: "📣", color: "bg-accent" }
  ];

  // Calculate progress percentage
  const progressPercentage = ((currentSection + 1) / sections.length) * 100;

  // Update form data
  const updateQuizData = (field: string, value: any) => {
    setQuizData(prev => ({ ...prev, [field]: value }));
  };

  // Handle multi-select updates
  const toggleMultiSelect = (field: string, value: string) => {
    setQuizData(prev => {
      const currentArray = prev[field as keyof typeof prev] as string[];
      return {
        ...prev,
        [field]: currentArray.includes(value)
          ? currentArray.filter(item => item !== value)
          : [...currentArray, value]
      };
    });
  };

  // Navigation functions
  const nextSection = () => {
    if (currentSection < sections.length - 1) {
      setCurrentSection(curr => curr + 1);
    } else {
      handleSubmitQuiz();
    }
  };

  const prevSection = () => {
    if (currentSection > 0) {
      setCurrentSection(curr => curr - 1);
    }
  };

  // Submit quiz
  const handleSubmitQuiz = async () => {
    setIsSubmitting(true);
    
    try {
      // TODO: Add Supabase integration to store quiz data
      console.log("Quiz completion:", quizData);
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      setShowResults(true);
      
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Something went wrong",
        description: "Please try again or contact support."
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Results screen
  if (showResults) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/20 via-secondary/20 to-accent/20 flex items-center justify-center p-4">
        <Card className="max-w-lg mx-auto text-center border-none shadow-2xl">
          <CardHeader className="pb-4">
            <div className="flex justify-center mb-4">
              <div className="w-20 h-20 bg-primary rounded-full flex items-center justify-center animate-bounce">
                <Trophy className="w-10 h-10 text-primary-foreground" />
              </div>
            </div>
            <CardTitle className="text-3xl font-bold text-foreground">
              You're Amazing! 🎉
            </CardTitle>
            <p className="text-lg text-muted-foreground">
              Your Money Personality: <span className="font-bold text-primary">Future Financial Boss</span>
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-muted/50 rounded-lg p-4">
              <p className="font-medium text-foreground mb-2">What happens next?</p>
              <ul className="text-sm text-muted-foreground space-y-1 text-left">
                <li>✅ You're on our priority waitlist</li>
                <li>✅ Personalized onboarding ready</li>
                <li>✅ Early access to beta features</li>
                <li>✅ Entered into £100 prize draw</li>
              </ul>
            </div>
            
            <div className="space-y-3">
              <Button 
                onClick={() => navigate("/")}
                className="w-full bg-primary hover:bg-primary/90"
              >
                Back to Home
              </Button>
              
              <p className="text-sm text-muted-foreground">
                🔗 <span className="font-medium">Share with friends</span> for bonus entries!
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/10 via-secondary/10 to-accent/10">
      {/* Header with progress */}
      <div className="sticky top-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border">
        <div className="container max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/")}
              className="text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            
            <div className="text-center">
              <h1 className="text-lg font-bold text-foreground">Money Personality Quiz</h1>
              <p className="text-sm text-muted-foreground">
                {sections[currentSection].icon} {sections[currentSection].title}
              </p>
            </div>
            
            <div className="text-sm text-muted-foreground">
              {currentSection + 1}/{sections.length}
            </div>
          </div>
          
          <Progress value={progressPercentage} className="h-2" />
        </div>
      </div>

      {/* Quiz content */}
      <div className="container max-w-2xl mx-auto px-4 py-8">
        <Card className="border-none shadow-lg animate-fade-in">
          <CardHeader className="text-center pb-6">
            <div className="text-4xl mb-2">{sections[currentSection].icon}</div>
            <CardTitle className="text-2xl font-bold text-foreground">
              {sections[currentSection].title}
            </CardTitle>
          </CardHeader>
          
          <CardContent className="space-y-6">
            {/* Section 1: The Basics */}
            {currentSection === 0 && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">What's your name?</label>
                  <Input
                    placeholder="Your first name"
                    value={quizData.name}
                    onChange={(e) => updateQuizData("name", e.target.value)}
                  />
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">Email address</label>
                  <Input
                    type="email"
                    placeholder="your.email@example.com"
                    value={quizData.email}
                    onChange={(e) => updateQuizData("email", e.target.value)}
                  />
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">How old are you?</label>
                  <Select value={quizData.age} onValueChange={(value) => updateQuizData("age", value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select your age range" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="16-18">16-18</SelectItem>
                      <SelectItem value="19-21">19-21</SelectItem>
                      <SelectItem value="22-24">22-24</SelectItem>
                      <SelectItem value="25-27">25-27</SelectItem>
                      <SelectItem value="28+">28+</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">Where are you based?</label>
                  <Input
                    placeholder="City, Region (e.g. Manchester, UK)"
                    value={quizData.location}
                    onChange={(e) => updateQuizData("location", e.target.value)}
                  />
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">Gender (Optional)</label>
                  <Select value={quizData.gender} onValueChange={(value) => updateQuizData("gender", value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Prefer not to say" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="non-binary">Non-binary</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                      <SelectItem value="prefer-not-to-say">Prefer not to say</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* Section 2: Money Personality */}
            {currentSection === 1 && (
              <div className="space-y-6">
                <div className="space-y-3">
                  <label className="text-sm font-medium text-foreground">What's your relationship with money?</label>
                  <div className="grid grid-cols-1 gap-2">
                    {[
                      { emoji: "😰", text: "It stresses me out", value: "stressed" },
                      { emoji: "🤔", text: "I'm learning", value: "learning" },
                      { emoji: "😊", text: "I'm pretty confident", value: "confident" },
                      { emoji: "🤑", text: "I love managing it", value: "love-it" },
                      { emoji: "😴", text: "I avoid thinking about it", value: "avoidant" }
                    ].map((option) => (
                      <Badge
                        key={option.value}
                        variant={quizData.moneyRelationship === option.value ? "default" : "outline"}
                        className={`cursor-pointer transition-all duration-200 p-3 text-left justify-start ${
                          quizData.moneyRelationship === option.value 
                            ? "bg-primary text-primary-foreground" 
                            : "hover:bg-muted"
                        }`}
                        onClick={() => updateQuizData("moneyRelationship", option.value)}
                      >
                        <span className="mr-2">{option.emoji}</span>
                        {option.text}
                      </Badge>
                    ))}
                  </div>
                </div>

                <div className="space-y-3">
                  <label className="text-sm font-medium text-foreground">Ever used a money app? (Select all that apply)</label>
                  <div className="grid grid-cols-2 gap-2">
                    {["Monzo", "Revolut", "Starling", "NatWest", "Lloyds", "HSBC", "None"].map((app) => (
                      <div key={app} className="flex items-center space-x-2">
                        <Checkbox
                          id={app}
                          checked={quizData.moneyApps.includes(app)}
                          onCheckedChange={() => toggleMultiSelect("moneyApps", app)}
                        />
                        <label htmlFor={app} className="text-sm cursor-pointer">{app}</label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-3">
                  <label className="text-sm font-medium text-foreground">Who do you bank with?</label>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { name: "Monzo", value: "monzo", domain: "monzo.com" },
                      { name: "Revolut", value: "revolut", domain: "revolut.com" },
                      { name: "Starling", value: "starling", domain: "starlingbank.com" },
                      { name: "NatWest", value: "natwest", domain: "natwest.com" },
                      { name: "Lloyds", value: "lloyds", domain: "lloydsbank.com" },
                      { name: "HSBC", value: "hsbc", domain: "hsbc.co.uk" },
                      { name: "Barclays", value: "barclays", domain: "barclays.co.uk" },
                      { name: "Santander", value: "santander", domain: "santander.co.uk" },
                      { name: "Other", value: "other", domain: "" }
                    ].map((bank) => (
                      <Badge
                        key={bank.value}
                        variant={quizData.bankProvider === bank.value ? "default" : "outline"}
                        className={`cursor-pointer transition-all duration-200 p-3 text-left justify-start gap-3 h-auto ${
                          quizData.bankProvider === bank.value 
                            ? "bg-primary text-primary-foreground" 
                            : "hover:bg-muted"
                        }`}
                        onClick={() => updateQuizData("bankProvider", bank.value)}
                      >
                        {bank.domain && (
                          <img 
                            src={`https://logo.clearbit.com/${bank.domain}?size=32`}
                            alt={`${bank.name} logo`}
                            className="w-6 h-6 rounded-sm object-contain"
                            onError={(e) => {
                              // Fallback to a generic bank icon if logo fails to load
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                        )}
                        {!bank.domain && <span className="w-6 h-6 flex items-center justify-center text-lg">🏛️</span>}
                        <span className="text-sm font-medium">{bank.name}</span>
                      </Badge>
                    ))}
                  </div>
                </div>

                <div className="space-y-3">
                  <label className="text-sm font-medium text-foreground">How often do you check your balance?</label>
                  <div className="grid grid-cols-1 gap-2">
                    {[
                      "Multiple times daily",
                      "Once a day",
                      "Few times a week", 
                      "Once a week",
                      "Rarely/never"
                    ].map((freq) => (
                      <Badge
                        key={freq}
                        variant={quizData.balanceCheckFreq === freq ? "default" : "outline"}
                        className={`cursor-pointer transition-all duration-200 p-3 text-left justify-start ${
                          quizData.balanceCheckFreq === freq 
                            ? "bg-primary text-primary-foreground" 
                            : "hover:bg-muted"
                        }`}
                        onClick={() => updateQuizData("balanceCheckFreq", freq)}
                      >
                        {freq}
                      </Badge>
                    ))}
                  </div>
                </div>

                <div className="space-y-3">
                  <label className="text-sm font-medium text-foreground">
                    Rate your money knowledge (1-10): {quizData.moneyKnowledge[0]}
                  </label>
                  <Slider
                    value={quizData.moneyKnowledge}
                    onValueChange={(value) => updateQuizData("moneyKnowledge", value)}
                    max={10}
                    min={1}
                    step={1}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>Complete beginner</span>
                    <span>Money expert</span>
                  </div>
                </div>

                <div className="space-y-3">
                  <label className="text-sm font-medium text-foreground">Have you been taught about money in school or at home?</label>
                  <div className="flex gap-4">
                    {["Yes", "No", "A little"].map((option) => (
                      <Badge
                        key={option}
                        variant={quizData.moneyEducation === option ? "default" : "outline"}
                        className={`cursor-pointer transition-all duration-200 px-6 py-2 ${
                          quizData.moneyEducation === option 
                            ? "bg-primary text-primary-foreground" 
                            : "hover:bg-muted"
                        }`}
                        onClick={() => updateQuizData("moneyEducation", option)}
                      >
                        {option}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Section 3: Dreams & Goals */}
            {currentSection === 2 && (
              <div className="space-y-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">What's your dream job?</label>
                  <Input
                    placeholder="e.g. Software Developer, Teacher, Entrepreneur"
                    value={quizData.dreamJob}
                    onChange={(e) => updateQuizData("dreamJob", e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">What's your dream salary?</label>
                  <Select value={quizData.dreamSalary} onValueChange={(value) => updateQuizData("dreamSalary", value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select salary range" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="20k-30k">£20k - £30k</SelectItem>
                      <SelectItem value="30k-50k">£30k - £50k</SelectItem>
                      <SelectItem value="50k-75k">£50k - £75k</SelectItem>
                      <SelectItem value="75k-100k">£75k - £100k</SelectItem>
                      <SelectItem value="100k+">£100k+</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">What age do you want to retire by?</label>
                  <Input
                    type="number"
                    placeholder="e.g. 65"
                    value={quizData.retireAge}
                    onChange={(e) => updateQuizData("retireAge", e.target.value)}
                  />
                </div>

                <div className="space-y-3">
                  <label className="text-sm font-medium text-foreground">Dream house?</label>
                  <div className="grid grid-cols-1 gap-2">
                    {[
                      { emoji: "🏠", text: "Cozy family home", value: "family-home" },
                      { emoji: "🏢", text: "City apartment", value: "apartment" },
                      { emoji: "🏰", text: "Luxury mansion", value: "mansion" },
                      { emoji: "🏡", text: "Countryside cottage", value: "cottage" },
                      { emoji: "🚐", text: "Van life / traveling", value: "nomad" }
                    ].map((option) => (
                      <Badge
                        key={option.value}
                        variant={quizData.dreamHouse === option.value ? "default" : "outline"}
                        className={`cursor-pointer transition-all duration-200 p-3 text-left justify-start ${
                          quizData.dreamHouse === option.value 
                            ? "bg-primary text-primary-foreground" 
                            : "hover:bg-muted"
                        }`}
                        onClick={() => updateQuizData("dreamHouse", option.value)}
                      >
                        <span className="mr-2">{option.emoji}</span>
                        {option.text}
                      </Badge>
                    ))}
                  </div>
                </div>

                <div className="space-y-3">
                  <label className="text-sm font-medium text-foreground">Dream car (or transportation)?</label>
                  <div className="grid grid-cols-1 gap-2">
                    {[
                      { emoji: "🚗", text: "Reliable family car", value: "family-car" },
                      { emoji: "🏎️", text: "Sports car", value: "sports-car" },
                      { emoji: "🚲", text: "Bike is enough", value: "bike" },
                      { emoji: "🚌", text: "Public transport", value: "public-transport" },
                      { emoji: "⚡", text: "Electric vehicle", value: "electric" }
                    ].map((option) => (
                      <Badge
                        key={option.value}
                        variant={quizData.dreamCar === option.value ? "default" : "outline"}
                        className={`cursor-pointer transition-all duration-200 p-3 text-left justify-start ${
                          quizData.dreamCar === option.value 
                            ? "bg-primary text-primary-foreground" 
                            : "hover:bg-muted"
                        }`}
                        onClick={() => updateQuizData("dreamCar", option.value)}
                      >
                        <span className="mr-2">{option.emoji}</span>
                        {option.text}
                      </Badge>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4">
                  <div className="space-y-3">
                    <label className="text-sm font-medium text-foreground">Do you want to start a business someday?</label>
                    <div className="flex gap-4">
                      {["Yes", "Maybe", "No"].map((option) => (
                        <Badge
                          key={option}
                          variant={quizData.startBusiness === option ? "default" : "outline"}
                          className={`cursor-pointer transition-all duration-200 px-6 py-2 ${
                            quizData.startBusiness === option 
                              ? "bg-primary text-primary-foreground" 
                              : "hover:bg-muted"
                          }`}
                          onClick={() => updateQuizData("startBusiness", option)}
                        >
                          {option}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-3">
                    <label className="text-sm font-medium text-foreground">Do you want to invest someday?</label>
                    <div className="flex gap-4">
                      {["Yes", "Maybe", "No"].map((option) => (
                        <Badge
                          key={option}
                          variant={quizData.wantToInvest === option ? "default" : "outline"}
                          className={`cursor-pointer transition-all duration-200 px-6 py-2 ${
                            quizData.wantToInvest === option 
                              ? "bg-primary text-primary-foreground" 
                              : "hover:bg-muted"
                          }`}
                          onClick={() => updateQuizData("wantToInvest", option)}
                        >
                          {option}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-3">
                    <label className="text-sm font-medium text-foreground">Are you interested in crypto or NFTs?</label>
                    <div className="flex gap-4">
                      {["Yes", "Curious", "No"].map((option) => (
                        <Badge
                          key={option}
                          variant={quizData.cryptoInterest === option ? "default" : "outline"}
                          className={`cursor-pointer transition-all duration-200 px-6 py-2 ${
                            quizData.cryptoInterest === option 
                              ? "bg-primary text-primary-foreground" 
                              : "hover:bg-muted"
                          }`}
                          onClick={() => updateQuizData("cryptoInterest", option)}
                        >
                          {option}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Section 4: Current Situation */}
            {currentSection === 3 && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 gap-4">
                  <div className="space-y-3">
                    <label className="text-sm font-medium text-foreground">Do you currently save money?</label>
                    <div className="flex gap-4">
                      {["Yes, regularly", "Sometimes", "No"].map((option) => (
                        <Badge
                          key={option}
                          variant={quizData.currentlySaving === option ? "default" : "outline"}
                          className={`cursor-pointer transition-all duration-200 px-4 py-2 ${
                            quizData.currentlySaving === option 
                              ? "bg-primary text-primary-foreground" 
                              : "hover:bg-muted"
                          }`}
                          onClick={() => updateQuizData("currentlySaving", option)}
                        >
                          {option}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-3">
                    <label className="text-sm font-medium text-foreground">
                      How much do you save per month? £{quizData.monthlySavings[0]}
                    </label>
                    <Slider
                      value={quizData.monthlySavings}
                      onValueChange={(value) => updateQuizData("monthlySavings", value)}
                      max={500}
                      min={0}
                      step={25}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>£0</span>
                      <span>£500+</span>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <label className="text-sm font-medium text-foreground">Do you track your spending?</label>
                    <div className="flex gap-4">
                      {["Yes, closely", "Somewhat", "No"].map((option) => (
                        <Badge
                          key={option}
                          variant={quizData.trackSpending === option ? "default" : "outline"}
                          className={`cursor-pointer transition-all duration-200 px-4 py-2 ${
                            quizData.trackSpending === option 
                              ? "bg-primary text-primary-foreground" 
                              : "hover:bg-muted"
                          }`}
                          onClick={() => updateQuizData("trackSpending", option)}
                        >
                          {option}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-3">
                    <label className="text-sm font-medium text-foreground">
                      How confident are you in budgeting? {quizData.budgetConfidence[0]}/10
                    </label>
                    <Slider
                      value={quizData.budgetConfidence}
                      onValueChange={(value) => updateQuizData("budgetConfidence", value)}
                      max={10}
                      min={1}
                      step={1}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>Not confident</span>
                      <span>Very confident</span>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <label className="text-sm font-medium text-foreground">Any debt right now?</label>
                    <div className="flex gap-4">
                      {["No debt", "Student loans", "Credit cards", "Other"].map((option) => (
                        <Badge
                          key={option}
                          variant={quizData.hasDebt === option ? "default" : "outline"}
                          className={`cursor-pointer transition-all duration-200 px-4 py-2 ${
                            quizData.hasDebt === option 
                              ? "bg-primary text-primary-foreground" 
                              : "hover:bg-muted"
                          }`}
                          onClick={() => updateQuizData("hasDebt", option)}
                        >
                          {option}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-3">
                    <label className="text-sm font-medium text-foreground">Have you invested before?</label>
                    <div className="flex gap-4">
                      {["No", "Stocks/shares", "Crypto", "ISAs/savings"].map((option) => (
                        <Badge
                          key={option}
                          variant={quizData.hasInvested === option ? "default" : "outline"}
                          className={`cursor-pointer transition-all duration-200 px-4 py-2 ${
                            quizData.hasInvested === option 
                              ? "bg-primary text-primary-foreground" 
                              : "hover:bg-muted"
                          }`}
                          onClick={() => updateQuizData("hasInvested", option)}
                        >
                          {option}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Section 5: Preferences */}
            {currentSection === 4 && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 gap-4">
                  <div className="space-y-3">
                    <label className="text-sm font-medium text-foreground">Would you like an AI assistant to help with your money?</label>
                    <div className="flex gap-4">
                      {["Yes, definitely", "Maybe", "No thanks"].map((option) => (
                        <Badge
                          key={option}
                          variant={quizData.wantAiAssistant === option ? "default" : "outline"}
                          className={`cursor-pointer transition-all duration-200 px-4 py-2 ${
                            quizData.wantAiAssistant === option 
                              ? "bg-primary text-primary-foreground" 
                              : "hover:bg-muted"
                          }`}
                          onClick={() => updateQuizData("wantAiAssistant", option)}
                        >
                          {option}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-3">
                    <label className="text-sm font-medium text-foreground">Would you connect your bank for insights like "Spotify Wrapped for money"?</label>
                    <div className="flex gap-4">
                      {["Yes, sounds cool", "Maybe", "No way"].map((option) => (
                        <Badge
                          key={option}
                          variant={quizData.bankConnection === option ? "default" : "outline"}
                          className={`cursor-pointer transition-all duration-200 px-4 py-2 ${
                            quizData.bankConnection === option 
                              ? "bg-primary text-primary-foreground" 
                              : "hover:bg-muted"
                          }`}
                          onClick={() => updateQuizData("bankConnection", option)}
                        >
                          {option}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-3">
                    <label className="text-sm font-medium text-foreground">Would you like a financial simulation game inside the app?</label>
                    <div className="flex gap-4">
                      {["Yes!", "Maybe", "Not interested"].map((option) => (
                        <Badge
                          key={option}
                          variant={quizData.financialGame === option ? "default" : "outline"}
                          className={`cursor-pointer transition-all duration-200 px-4 py-2 ${
                            quizData.financialGame === option 
                              ? "bg-primary text-primary-foreground" 
                              : "hover:bg-muted"
                          }`}
                          onClick={() => updateQuizData("financialGame", option)}
                        >
                          {option}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-3">
                    <label className="text-sm font-medium text-foreground">What features excite you most? (Select all that apply)</label>
                    <div className="grid grid-cols-2 gap-2">
                      {["Learn", "Simulate", "Goals", "Social", "Investing", "Budgeting"].map((feature) => (
                        <div key={feature} className="flex items-center space-x-2">
                          <Checkbox
                            id={feature}
                            checked={quizData.excitingFeatures.includes(feature)}
                            onCheckedChange={() => toggleMultiSelect("excitingFeatures", feature)}
                          />
                          <label htmlFor={feature} className="text-sm cursor-pointer">{feature}</label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Section 6: Spread the Word */}
            {currentSection === 5 && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 gap-4">
                  <div className="space-y-3">
                    <label className="text-sm font-medium text-foreground">Want early access + perks for referring friends?</label>
                    <div className="flex gap-4">
                      {["Yes, absolutely", "Maybe", "No thanks"].map((option) => (
                        <Badge
                          key={option}
                          variant={quizData.referralInterest === option ? "default" : "outline"}
                          className={`cursor-pointer transition-all duration-200 px-4 py-2 ${
                            quizData.referralInterest === option 
                              ? "bg-primary text-primary-foreground" 
                              : "hover:bg-muted"
                          }`}
                          onClick={() => updateQuizData("referralInterest", option)}
                        >
                          {option}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">Drop your TikTok or Instagram @ (Optional)</label>
                    <Input
                      placeholder="@yourusername (optional)"
                      value={quizData.socialHandle}
                      onChange={(e) => updateQuizData("socialHandle", e.target.value)}
                    />
                  </div>

                  <div className="space-y-3">
                    <label className="text-sm font-medium text-foreground">Would you be down to test the beta?</label>
                    <div className="flex gap-4">
                      {["Yes, count me in", "Maybe", "No"].map((option) => (
                        <Badge
                          key={option}
                          variant={quizData.betaTester === option ? "default" : "outline"}
                          className={`cursor-pointer transition-all duration-200 px-4 py-2 ${
                            quizData.betaTester === option 
                              ? "bg-primary text-primary-foreground" 
                              : "hover:bg-muted"
                          }`}
                          onClick={() => updateQuizData("betaTester", option)}
                        >
                          {option}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-3">
                    <label className="text-sm font-medium text-foreground">Want to join our Gen Z ambassador squad?</label>
                    <div className="flex gap-4">
                      {["Yes, I'm in", "Tell me more", "No thanks"].map((option) => (
                        <Badge
                          key={option}
                          variant={quizData.ambassador === option ? "default" : "outline"}
                          className={`cursor-pointer transition-all duration-200 px-4 py-2 ${
                            quizData.ambassador === option 
                              ? "bg-primary text-primary-foreground" 
                              : "hover:bg-muted"
                          }`}
                          onClick={() => updateQuizData("ambassador", option)}
                        >
                          {option}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="bg-accent/10 rounded-lg p-4 text-center">
                  <p className="text-sm text-muted-foreground mb-2">
                    🎉 <span className="font-medium">Almost done!</span>
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Complete the quiz to enter our £100 prize draw and get personalized results!
                  </p>
                </div>
              </div>
            )}
            
            {/* Navigation buttons */}
            <div className="flex justify-between pt-6 border-t border-border">
              <Button
                variant="outline"
                onClick={prevSection}
                disabled={currentSection === 0}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Previous
              </Button>
              
              <Button
                onClick={nextSection}
                disabled={isSubmitting}
                className="flex items-center gap-2 bg-primary hover:bg-primary/90"
              >
                {isSubmitting ? "Submitting..." : currentSection === sections.length - 1 ? "Complete Quiz" : "Next"}
                {!isSubmitting && <ArrowRight className="w-4 h-4" />}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};